/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import { tokenStorage } from '../utils/storage';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user, isAuth } = useAuth();
  const [connection, setConnection] = useState(null);
  const [connected, setConnected] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeMessages, setActiveMessages] = useState({});
  const listeners = useRef({});

  useEffect(() => {
      if (!isAuth || !user) return;

      const useHosted = import.meta.env.VITE_USE_HOSTED_API === 'true';
      
      const isLocalHost = (hostname) => {
        return (
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          hostname === '10.0.2.2' ||
          hostname.startsWith('192.168.') ||
          hostname.startsWith('172.') ||
          hostname.startsWith('10.')
        );
      };

      const isLocal = !useHosted && isLocalHost(window.location.hostname);
      const hubUrl = isLocal
          ? `http://${window.location.hostname}:5000/hubs/chat`
          : 'https://shopaxo.in/API/hubs/chat';

      const conn = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
          accessTokenFactory: async () => (await tokenStorage.getItem('accessToken')) || '',
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    // Handle incoming messages
    conn.on('ReceiveMessage', (message) => {
      const convId = message.conversationId;
      setActiveMessages(prev => ({
        ...prev,
        [convId]: [...(prev[convId] || []), { ...message, isMine: message.senderId === user.userId }]
      }));

      // Update conversation last message
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, lastMessage: message, updatedAt: message.createdAt } : c
      ));

      // Trigger listeners
      Object.values(listeners.current).forEach(fn => fn('ReceiveMessage', message));
    });

    conn.on('MessageEdited', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, content: data.newContent } : m)
        };
      });
      Object.values(listeners.current).forEach(fn => fn('MessageEdited', data));
    });

    conn.on('MessageDeleted', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, isDeleted: true, content: '🚫 This message was deleted' } : m)
        };
      });
      Object.values(listeners.current).forEach(fn => fn('MessageDeleted', data));
    });

    conn.on('MessageReacted', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m)
        };
      });
      Object.values(listeners.current).forEach(fn => fn('MessageReacted', data));
    });

    conn.on('UserTyping', (data) => {
      Object.values(listeners.current).forEach(fn => fn('UserTyping', data));
    });

    conn.on('MessagesDelivered', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => {
            if (m.senderId !== data.userId && m.status === 'Sent') {
              return { ...m, status: 'Delivered' };
            }
            return m;
          })
        };
      });

      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId && c.lastMessage && c.lastMessage.senderId !== data.userId && c.lastMessage.status === 'Sent') {
          return { ...c, lastMessage: { ...c.lastMessage, status: 'Delivered' } };
        }
        return c;
      }));

      Object.values(listeners.current).forEach(fn => fn('MessagesDelivered', data));
    });

    conn.on('MessageRead', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => {
            if (m.senderId !== data.userId && (m.status === 'Sent' || m.status === 'Delivered')) {
              return { ...m, status: 'Read' };
            }
            return m;
          })
        };
      });

      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId && c.lastMessage && c.lastMessage.senderId !== data.userId && c.lastMessage.status !== 'Read') {
          return { ...c, lastMessage: { ...c.lastMessage, status: 'Read' } };
        }
        return c;
      }));

      Object.values(listeners.current).forEach(fn => fn('MessageRead', data));
    });

      conn.on('OrderUpdated', (data) => {
          console.log('ORDER UPDATED', data);
          Object.values(listeners.current).forEach(fn => fn('OrderUpdated', data));
      });

    conn.on('ReceiveNotification', (notification) => {
      Object.values(listeners.current).forEach(fn => fn('ReceiveNotification', notification));
    });

    conn.onreconnected(() => setConnected(true));
    conn.onclose(() => setConnected(false));

    conn.start()
      .then(() => { setConnected(true); setConnection(conn); })
      .catch(err => console.warn('[SignalR] Connection failed:', err));

    return () => {
      conn.stop();
      setConnected(false);
    };
  }, [isAuth, user]);

  const sendMessage = useCallback(async (conversationId, content, type = 'Text', refId = null, mediaUrl = null) => {
    if (!connection || !connected) return;
    await connection.invoke('SendMessage', conversationId, content, type, refId, mediaUrl);
  }, [connection, connected]);

  const markRead = useCallback(async (conversationId) => {
    if (!connection || !connected) return;
    await connection.invoke('MarkRead', conversationId);
  }, [connection, connected]);

  const startTyping = useCallback(async (conversationId) => {
    if (!connection || !connected) return;
    await connection.invoke('StartTyping', conversationId);
  }, [connection, connected]);

  const stopTyping = useCallback(async (conversationId) => {
    if (!connection || !connected) return;
    await connection.invoke('StopTyping', conversationId);
  }, [connection, connected]);

  const joinConversation = useCallback(async (conversationId) => {
    if (!connection || !connected) return;
    await connection.invoke('JoinConversation', conversationId);
  }, [connection, connected]);

  const addListener = useCallback((key, fn) => {
    listeners.current[key] = fn;
    return () => delete listeners.current[key];
  }, []);

  const addMessages = useCallback((convId, messages) => {
    setActiveMessages(prev => ({ ...prev, [convId]: messages }));
  }, []);

  const prependMessages = useCallback((convId, newMessages) => {
    setActiveMessages(prev => ({
      ...prev,
      [convId]: [...newMessages, ...(prev[convId] || [])]
    }));
  }, []);

  return (
    <ChatContext.Provider value={{
      connected, conversations, setConversations, activeMessages, addMessages, prependMessages,
      sendMessage, markRead, startTyping, stopTyping, addListener, joinConversation
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
